/** @jsxImportSource react */
import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/firebase';
import { clearAuthToken } from '@/utils/tokenValidation';
import { getImpersonationTokenKey } from '@/utils/authToken';
import { getRoleFromToken } from '@/utils/tokenValidation';
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext';
import { useNotifications, NotificationProvider, STORAGE_KEY_ADMIN } from '@/contexts/NotificationContext';
import { BackgroundParticles } from '../shared/BackgroundParticles';
import { LogoInsaite } from '../shared/LogoInsaite';
import { getNotificationIcon } from '../shared/NotificationIcons';
import '../recruiter/RecruiterLayout.css';
import './AdminLayout.css';

const globeIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width={20} height={20}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
    <ellipse cx="12" cy="12" rx="10" ry="4" stroke="currentColor" strokeWidth="1.5" transform="rotate(90 12 12)" />
    <path d="M2 12H22" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
const bellIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width={22} height={22}>
    <path d="M18 8A6 6 0 0 0 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function getInitials(name: string): string {
  if (!name) return '?';
  const names = name.trim().split(/\s+/);
  const initials = names.map((n) => n[0]).join('');
  return initials.slice(0, 2).toUpperCase();
}

const SIDEBAR_WIDTH = 280;

const dashboardIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
  </svg>
);
const usersIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const vacanciesIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M2 13h20" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
const configIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" fill="none" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" fill="none" />
  </svg>
);
const auditIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const aiMonitoringIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2a4 4 0 0 1 4 4v2h2a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2V6a4 4 0 0 1 4-4z" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M9 14l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const logoutIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 12H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** Comprueba de forma síncrona si hay token de impersonación (evita montar hijos que llamen /api/admin/* con ese token). */
function hasImpersonationToken(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  const key = getImpersonationTokenKey();
  const v = sessionStorage.getItem(key);
  return !!(v && v.trim().length > 0);
}

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, theme, setTheme, lang, setLang } = useThemeLanguage();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [user, setUser] = useState<{ displayName?: string | null; email?: string | null; photoURL?: string | null } | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u ? { displayName: u.displayName, email: u.email, photoURL: u.photoURL } : null);
    });
    return () => unsubscribe();
  }, []);

  const isImpersonating = hasImpersonationToken();

  // Si hay token de impersonación, redirigir al destino según el rol impersonado (reclutador → /recruiter, candidato → /home)
  useEffect(() => {
    if (!isImpersonating) return;
    const impKey = getImpersonationTokenKey();
    const impToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(impKey) : null;
    if (!impToken) return;
    const role = getRoleFromToken(impToken);
    const target = role === 'reclutador' ? '/recruiter' : '/home';
    navigate(target, { replace: true });
  }, [isImpersonating, navigate]);

  // Antes estaba después del `return` de abajo: en los renders con
  // impersonación activa el hook no se ejecutaba y los slots de React se
  // desalineaban. Mismo fallo que tumbó el flujo del candidato.
  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  if (isImpersonating) {
    return (
      <div className="recruiter-layout admin-layout-wrap" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p className="admin-loading">{t('admin.redirecting')}</p>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.warn('Firebase signOut:', e);
    }
    clearAuthToken(false);
    navigate('/', { replace: true });
  };

  const navItem = (to: string, label: string, icon: React.ReactNode, exact?: boolean) => {
    const active = exact ? location.pathname === to : location.pathname.startsWith(to);
    return (
      <NavLink
        to={to}
        className={`recruiter-sidebar-nav-item ${active ? 'active' : ''}`}
        end={exact ?? (to === '/admin' || to === '/admin/')}
      >
        <span className="nav-item-icon nav-item-icon--expanded">{icon}</span>
        {!isSidebarCollapsed && <span className="nav-item-text">{label}</span>}
      </NavLink>
    );
  };

  const sidebarWidth = isSidebarCollapsed ? 56 : SIDEBAR_WIDTH;
  const accountLabel = user?.displayName || user?.email || t('admin.account');
  const accountAvatar = user?.photoURL
    ? <img src={user.photoURL} alt="" className="recruiter-sidebar-account-avatar" />
    : <div className="recruiter-sidebar-account-initials">{getInitials(user?.displayName || user?.email || '')}</div>;

  return (
    <div data-theme={theme} className="recruiter-layout admin-layout-wrap" style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}>
      <div className="recruiter-layout-l-bg">
        <BackgroundParticles intensity={0.7} particleCount={40} colorSet="default" />
      </div>

      <aside className={`recruiter-sidebar recruiter-sidebar--no-bg ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="recruiter-sidebar-header">
          <button
            type="button"
            className="recruiter-sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={isSidebarCollapsed ? t('admin.sidebar.expand') : t('admin.sidebar.collapse')}
            title={isSidebarCollapsed ? t('admin.sidebar.expand') : t('admin.sidebar.collapse')}
          >
            {isSidebarCollapsed ? (
              <svg viewBox="0 0 24 24" fill="none" width={20} height={20}>
                <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" width={20} height={20}>
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
          <div className={`recruiter-sidebar-logo recruiter-sidebar-logo--${theme}`}>
            <LogoInsaite
              size={isSidebarCollapsed ? 'small' : 'medium'}
              position="relative"
              variant={theme === 'light-pure' ? 'dark' : theme === 'violet-inverted' ? 'violet' : 'light'}
              aiOnly={isSidebarCollapsed}
            />
          </div>
        </div>

        <div className="recruiter-sidebar-spacer" />

        <div className="recruiter-sidebar-bottom">
          <nav className="recruiter-sidebar-nav">
            {navItem('/admin', t('admin.dashboard'), dashboardIconSvg, true)}
            {navItem('/admin/users', t('admin.users'), usersIconSvg)}
            {/* El administrador también recluta: sin esta entrada no había forma
                de llegar a las vacantes desde el panel, y en una instancia de una
                sola persona el producto quedaba sin usar. */}
            {navItem('/recruiter', t('admin.vacancies'), vacanciesIconSvg)}
            {navItem('/admin/config', t('admin.config'), configIconSvg)}
            {navItem('/admin/audit', t('admin.audit'), auditIconSvg)}
            {navItem('/admin/ai-monitoring', t('admin.aiUsage'), aiMonitoringIconSvg)}
          </nav>
          <div className="recruiter-sidebar-footer">
            {user && (
              <div className={`recruiter-sidebar-account ${isSidebarCollapsed ? 'recruiter-sidebar-account--collapsed' : ''}`}>
                <div className="recruiter-sidebar-account-avatar-wrap">{accountAvatar}</div>
                {!isSidebarCollapsed && <span className="recruiter-sidebar-account-label">{accountLabel}</span>}
              </div>
            )}
            <button
              type="button"
              className="recruiter-sidebar-nav-item recruiter-sidebar-logout"
              onClick={handleLogout}
              aria-label={t('admin.logout')}
            >
              <span className="nav-item-icon nav-item-icon--expanded">{logoutIconSvg}</span>
              {!isSidebarCollapsed && <span className="nav-item-text">{t('admin.logout')}</span>}
            </button>
          </div>
        </div>
      </aside>

      <main className="recruiter-layout-main">
        <div className="recruiter-breadcrumbs-wrap admin-breadcrumbs">
          <span className="admin-breadcrumb-title">{t('admin.breadcrumb')}</span>
          <div className="recruiter-header-actions">
            <div className="recruiter-theme-selector">
              <button type="button" className={`theme-dot default ${theme === 'default' ? 'active' : ''}`} onClick={() => setTheme('default')} title={t('settings.theme.default')} />
              <button type="button" className={`theme-dot violet ${theme === 'violet' ? 'active' : ''}`} onClick={() => setTheme('violet')} title={t('settings.theme.violet')} />
              <button type="button" className={`theme-dot black ${theme === 'black' ? 'active' : ''}`} onClick={() => setTheme('black')} title={t('settings.theme.black')} />
              <button type="button" className={`theme-dot light-pure ${theme === 'light-pure' ? 'active' : ''}`} onClick={() => setTheme('light-pure')} title={t('settings.theme.lightPure')} />
              <button type="button" className={`theme-dot violet-inverted ${theme === 'violet-inverted' ? 'active' : ''}`} onClick={() => setTheme('violet-inverted')} title={t('settings.theme.violetInverted')} />
            </div>
            <button type="button" className="recruiter-lang-selector" onClick={() => setLang(lang === 'es' ? 'en' : 'es')} title={lang === 'es' ? t('candidate.switchToEnglish') : t('candidate.switchToSpanish')} aria-label={t('layout.changeLanguage')}>
              {globeIconSvg}
            </button>
            <div className="notification-bell-container">
              <button type="button" className={`notification-bell-btn ${unreadCount > 0 ? 'has-notifications' : ''}`} title={t('admin.notifications')} onClick={() => setShowNotifications(!showNotifications)}>
                {bellIconSvg}
                {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
              </button>
              {showNotifications && (
                <div className="notification-dropdown">
                  <div className="notification-dropdown-header">
                    <h3>{t('admin.notifications')}</h3>
                    <button type="button" onClick={markAllAsRead}>{t('admin.markAllRead')}</button>
                  </div>
                  <div className="notification-list">
                    {notifications.length === 0 ? (
                      <div className="notification-empty">{t('admin.noNotifications')}</div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`notification-item ${!n.read ? 'unread' : ''}`}
                          onClick={() => {
                            markAsRead(n.id);
                            if (n.vacanteId) navigate('/admin');
                            setShowNotifications(false);
                          }}
                        >
                          <div className="notification-item-icon">{getNotificationIcon(n.type)}</div>
                          <div className="notification-item-content">
                            <p className="notification-title">{n.titleKey ? t(n.titleKey) : n.title}</p>
                            <p className="notification-description">{n.messageKey ? t(n.messageKey) : n.message}</p>
                            <span className="notification-time">{new Date(n.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="recruiter-layout-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/** Wrapper que provee notificaciones con clave de admin (lista separada de reclutador). Usar en la ruta /admin. */
export function AdminLayoutWithNotifications() {
  return React.createElement(NotificationProvider, { storageKey: STORAGE_KEY_ADMIN, children: React.createElement(AdminLayout) });
}
